/**
 * Route Protection Components
 *
 * Wrappers for different route types:
 * - ProtectedRoute: any signed-in colleague
 * - AdminRoute: administrators only
 * - ToolRoute: a tool's page: administrators, and standard users unless the tool is for
 *   administrators or has been switched off for standard users
 * - PublicRoute: Uses anonymous auth
 * - AuthRoute: For login pages, redirects if already logged in
 *
 * ⚠️ Until 6 Oct 2026 `AdminRoute` checked only that someone was signed in ("individual
 * pages handle admin-only actions"), and nothing looked at Settings → tools for standard
 * users except the menu. So a switched-off tool, and every administrators' page, opened for
 * any colleague who had the address. Each guard now does what its name says, and
 * src/auth/__tests__/routes.test.js lists every route with who it lets in.
 *
 * ⚠️ These guards decide what the PAGE shows. They are not what keeps data safe: the
 * functions and the database rules do that, and they answer by role (colleague or
 * administrator), not by tool. See src/utils/toolAccess.js.
 */

import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { FaLock } from "react-icons/fa";
import { useAuth, useAnonymousAuth, AuthStates } from "./AuthContext";
import Spinner from "../components/Spinner";
import DashboardLayout from "../layouts/DashboardLayout";
import { EmptyState } from "../components/shared/EmptyState";
import { Button } from "../components/shared/Button";
import { toolRegistry } from "../tools/ToolRegistry";
import { useToolSwitches } from "../hooks/useToolSwitches";
import { toolAccess, refusalText, ALLOWED, ADMINS_ONLY } from "../utils/toolAccess";

/**
 * What a signed-in colleague sees in place of a page that is not open to them: the usual
 * frame, so the menu is still there, and one line saying why and what to do.
 */
export const NotAvailable = ({ title, description }) => {
  const navigate = useNavigate();
  return (
    <DashboardLayout>
      <EmptyState
        icon={<FaLock />}
        title={title}
        description={description}
        action={
          <Button variant="primary" onClick={() => navigate("/dashboard")}>
            Back to the dashboard
          </Button>
        }
      />
    </DashboardLayout>
  );
};

/**
 * Protected Route - Requires full authentication
 * Redirects to login if not authenticated
 */
export const ProtectedRoute = ({ children }) => {
  const { authState, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner text="Checking authentication..." />
      </div>
    );
  }

  if (authState !== AuthStates.AUTHENTICATED) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * Admin Route - administrators only.
 * Not signed in: to the sign-in page. Signed in and not an administrator: told so, in the
 * usual frame. The page inside is never drawn for them.
 */
export const AdminRoute = ({ children }) => {
  const { authState, isLoading, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner text="Checking permissions..." />
      </div>
    );
  }

  if (authState !== AuthStates.AUTHENTICATED) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <NotAvailable {...refusalText(ADMINS_ONLY)} />;
  }

  return children;
};

/**
 * Tool Route - the page of one registered tool (`toolId` as in src/tools).
 *
 * An administrator always gets the page. A standard user gets it unless the tool is for
 * administrators, or an administrator has switched it off for standard users in Settings.
 * The rule is src/utils/toolAccess.js; the switch is read by useToolSwitches, and the page
 * is not drawn until it has been.
 */
export const ToolRoute = ({ toolId, children }) => {
  const { authState, isLoading, isAdmin } = useAuth();
  const location = useLocation();
  const signedIn = !isLoading && authState === AuthStates.AUTHENTICATED;
  const tool = toolRegistry.get(toolId) || null;
  // Only a standard user's answer can depend on the switch. Read it for nobody else.
  const needsSwitches = signedIn && !isAdmin && Boolean(tool) && !tool.requiresAdmin;
  const switches = useToolSwitches(needsSwitches);

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner text="Checking permissions..." />
      </div>
    );
  }

  if (authState !== AuthStates.AUTHENTICATED) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsSwitches && switches.status === "loading") {
    return (
      <div className="auth-loading">
        <Spinner text="Checking permissions..." />
      </div>
    );
  }

  const access = toolAccess(tool, { isAdmin }, switches.tools);
  if (access !== ALLOWED) {
    return <NotAvailable {...refusalText(access, tool)} />;
  }

  return children;
};

/**
 * Public Route - Uses anonymous auth
 * Automatically signs in anonymously if needed
 */
export const PublicRoute = ({ children }) => {
  const { isLoading } = useAnonymousAuth();

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner text="Loading..." />
      </div>
    );
  }

  return children;
};

/**
 * Auth Route - For login/signup pages
 * Redirects to welcome/dashboard if already authenticated
 */
export const AuthRoute = ({ children }) => {
  const { authState, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner text="Loading..." />
      </div>
    );
  }

  if (authState === AuthStates.AUTHENTICATED) {
    // Already logged in - redirect to intended page or welcome
    const from = location.state?.from?.pathname || "/welcome";
    return <Navigate to={from} replace />;
  }

  return children;
};

/**
 * Route wrapper that selects the appropriate protection based on type
 */
export const RouteGuard = ({ type, children }) => {
  switch (type) {
    case "protected":
      return <ProtectedRoute>{children}</ProtectedRoute>;
    case "admin":
      return <AdminRoute>{children}</AdminRoute>;
    case "auth":
      return <AuthRoute>{children}</AuthRoute>;
    case "public":
    default:
      return <PublicRoute>{children}</PublicRoute>;
  }
};

export default RouteGuard;
